<template>
	<div class="bento-tile learn-tile">
		<div class="bento-tile__header">
			<span class="bento-tile__eyebrow">Learn</span>
			<v-btn
				variant="text"
				size="small"
				:href="learnHref('https://docs.signaloid.io/', 'documentation_button')"
				target="_blank"
				rel="noopener"
				class="bento-tile__action"
			>
				Documentation
				<ArrowUpRight16 />
			</v-btn>
		</div>
		<div class="learn-tile__list">
			<template v-if="ready">
				<a
					v-for="(card, i) in cards"
					:key="card.id"
					:href="learnHref(card.href, card.id)"
					target="_blank"
					rel="noopener"
					class="learn-tile__row sgnl-text-color-secondary"
					:class="{ 'learn-tile__row--last': i === cards.length - 1 }"
					@click="
						$posthog?.capture('home_learn_card_clicked', {
							card_id: card.id,
							kind: card.kind,
							pinned: !!card.pinned,
						})
					"
				>
					<span class="learn-tile__title sgnl-text-color-secondary">
						<span
							v-if="card.pinned"
							class="learn-tile__kind learn-tile__kind--brand sgnl-caption-regular"
							>New</span
						>
						<span class="learn-tile__title-text sgnl-paragraph-s-regular">{{ card.title }}</span>
					</span>
					<ArrowUpRight16 class="learn-tile__title-icon" />
				</a>
			</template>
			<template v-else>
				<div
					v-for="i in skeletonRowCount"
					:key="i"
					class="learn-tile__row learn-tile__row--skeleton sgnl-text-color-secondary"
					:class="{ 'learn-tile__row--last': i === skeletonRowCount }"
					aria-hidden="true"
				>
					<SkeletonBlock
						class="sgnl-paragraph-s-regular"
						width="60%"
						radius="var(--sgnl-border-radius-4)"
					/>
				</div>
			</template>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import ArrowUpRight16 from "@carbon/icons-vue/es/arrow--up-right/16";

import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";
import { LEARN_CARDS, type LearnCard } from "@/components/Home/learnCards";

export type LearnTileUserState = {
	userId: string;
	hasApiKey: boolean;
	apiKeyCount: number;
	tasksRun: number;
	repos: number;
	dataSources: number;
	customCores: number;
};

const LEARN_CARD_LIMIT = 5;

// UTM tags on outbound doc links so the docs analytics can attribute the visit
// back to the home Learn tile (the in-app click is captured separately).
const LEARN_UTM = "utm_source=scdp&utm_medium=learn_tile";

// Exported for tests so we can assert seed/rotation sensitivity directly,
// instead of indirectly through the rendered card list (which couples to
// evergreen pool size and risks false negatives on mod-collision).
export const daySeed = (uid: string): number => {
	const day = new Date().toISOString().slice(0, 10);
	let h = 0;
	for (const ch of day + uid) h = ((h << 5) - h + ch.charCodeAt(0)) | 0;
	return Math.abs(h);
};

export default defineComponent({
	name: "LearnTile",
	components: { ArrowUpRight16, SkeletonBlock },
	props: {
		userState: {
			type: Object as PropType<LearnTileUserState>,
			required: true,
		},
		ready: {
			type: Boolean,
			default: true,
		},
	},
	computed: {
		skeletonRowCount(): number {
			return LEARN_CARD_LIMIT;
		},
		cards(): LearnCard[] {
			const s = this.userState;
			const seed = daySeed(s.userId || "anon");
			const picked: LearnCard[] = [];
			const used = new Set<string>();

			const pinned = LEARN_CARDS.filter((c) => c.pinned);
			for (const c of pinned) {
				if (picked.length >= LEARN_CARD_LIMIT) break;
				picked.push(c);
				used.add(c.id);
			}

			const triggered = LEARN_CARDS.filter((c) => !c.pinned && c.triggers && c.triggers(s)).sort(
				(a, b) => (a.priority ?? 99) - (b.priority ?? 99),
			);
			for (const c of triggered) {
				if (picked.length >= LEARN_CARD_LIMIT) break;
				if (!used.has(c.id)) {
					picked.push(c);
					used.add(c.id);
				}
			}

			const evergreen = LEARN_CARDS.filter((c) => !c.pinned && !c.triggers);
			let i = evergreen.length > 0 ? seed % evergreen.length : 0;
			let safety = evergreen.length * 2;
			while (picked.length < LEARN_CARD_LIMIT && safety-- > 0 && evergreen.length > 0) {
				const c = evergreen[i % evergreen.length];
				if (!used.has(c.id)) {
					picked.push(c);
					used.add(c.id);
				}
				i++;
			}

			return picked.slice(0, LEARN_CARD_LIMIT);
		},
	},
	methods: {
		learnHref(href: string, content: string): string {
			const sep = href.includes("?") ? "&" : "?";
			return `${href}${sep}${LEARN_UTM}&utm_content=${encodeURIComponent(content)}`;
		},
	},
});
</script>

<style lang="scss" scoped>
.learn-tile__list {
	display: flex;
	flex-direction: column;
}

.learn-tile__row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: var(--sgnl-space-8);
	padding: var(--sgnl-space-12) 0;
	border-bottom: var(--sgnl-border-width-1) solid var(--sgnl-default-tertiary-border);
	text-decoration: none;
}

.learn-tile__row--last {
	border-bottom: none;
}

.learn-tile__title {
	display: inline-flex;
	align-items: center;
	gap: var(--sgnl-space-6);
	min-width: 0;
	white-space: nowrap;
}

.learn-tile__title-text {
	overflow: hidden;
	text-overflow: ellipsis;
}

.learn-tile__title-icon {
	color: var(--sgnl-default-secondary-icon);
	flex-shrink: 0;
}

.learn-tile__row:hover .learn-tile__title-text {
	text-decoration: underline;
}

.learn-tile__row:hover .learn-tile__title-icon {
	opacity: 1;
}

.learn-tile__kind {
	display: inline-flex;
	align-items: center;
	height: var(--sgnl-unit-24);
	padding: 0 var(--sgnl-space-8);
	border-radius: var(--sgnl-border-radius-6);
	border: var(--sgnl-border-width-1) solid var(--sgnl-default-secondary-border);
	color: var(--sgnl-default-secondary-text);
	font-family: var(--sgnl-font-family-mono);
	flex-shrink: 0;
}

.learn-tile__kind--brand {
	background: var(--sgnl-brand-background-inverse);
	color: var(--sgnl-brand-text);
	border-color: var(--sgnl-brand-background-inverse);
}
</style>
